import { BellRing, Filter, MapPin, Plus, Search, Store } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getNurturings, getNurturingStats } from "../../interfaces/nurturings";
import { INurturing, NurturingStats, NurturingType } from "../../interfaces/nurturing";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Input } from "../../components/ui/Input";
import { StatCard } from "../../components/ui/Card";
import { Select } from "../../components/ui/Select";

const NURTURING_TYPE_LABELS: Record<NurturingType, string> = {
    owner_tracking: "Suivi propriétaire",
    group_tracking: "Suivi d'un groupe",
    franchise_tracking: "Suivi franchise",
    chef_tracking: "Suivi chef de cuisine",
    multisite_tracking: "Suivi multi-sites",
};

const STATUS_LABELS = {
    active: "Actif",
    paused: "Mis en pause",
    archived: "Archivé",
};

const PRIORITY_LABELS = {
    low: "Faible",
    medium: "Moyenne",
    high: "Haute",
};

export default function NurturingList() {
    const navigate = useNavigate();
    const [nurturings, setNurturings] = useState<INurturing[]>([]);
    const [stats, setStats] = useState<NurturingStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("active");
    const [typeFilter, setTypeFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, statusFilter, typeFilter, priorityFilter]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [nurturingsData, statsData] = await Promise.all([
                getNurturings({
                    search: searchQuery || undefined,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    type: typeFilter !== 'all' ? typeFilter : undefined,
                    priority: priorityFilter !== 'all' ? priorityFilter : undefined,
                    sortBy: 'lastEventAt',
                    order: 'desc',
                    limit: 100,
                }),
                getNurturingStats()
            ]);

            setNurturings(nurturingsData.data || []);
            setStats(statsData);
        } catch (err) {
            console.error("Failed to fetch nurturings:", err);
            setError("Échec du chargement des nurturings");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString?: string | Date) => {
        if (!dateString) return "Jamais";
        return new Date(dateString).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    const formatDateTime = (dateString?: string | Date) => {
        if (!dateString) return "Jamais";
        const date = new Date(dateString);
        return date.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getStatusTone = (status: string): "success" | "warning" | "neutral" => {
        if (status === "active") return "success";
        if (status === "paused") return "warning";
        return "neutral";
    };

    const getPriorityTone = (priority?: string): "danger" | "neutral" => {
        if (priority === "high") return "danger";
        return "neutral";
    };

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-6">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">Nurturing</h1>
                            <p className="text-sm text-ink-muted mt-1">
                                Suivi automatisé de vos cibles commerciales
                            </p>
                        </div>
                        <Button onClick={() => navigate("/nurturings/create")}>
                            <Plus className="h-4 w-4" />
                            <span>Nouveau</span>
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-4 gap-6 mb-6">
                        <StatCard label="Total" value={stats.total} />
                        <StatCard label="Actifs" value={stats.active} />
                        <StatCard label="En pause" value={stats.paused} />
                        <StatCard label="Alertes 7j" value={stats.recentAlerts} />
                    </div>
                )}

                {/* Filters */}
                <div className="rounded-2xl border border-border bg-surface p-4 mb-6">
                    <div className="flex flex-col md:flex-row gap-3">
                        {/* Search */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint" />
                            <Input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Rechercher..."
                                className="pl-9"
                            />
                        </div>

                        {/* Toggle Filters */}
                        <Button
                            variant={showFilters ? "primary" : "secondary"}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <Filter className="h-4 w-4" />
                            <span>Filtres</span>
                        </Button>
                    </div>

                    {/* Filter Options */}
                    {showFilters && (
                        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Statut
                                </label>
                                <Select
                                    fullWidth
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    options={[
                                        { value: "all", label: "Tous" },
                                        { value: "active", label: "Actif" },
                                        { value: "paused", label: "Pause" },
                                        { value: "archived", label: "Archivé" },
                                    ]}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Type
                                </label>
                                <Select
                                    fullWidth
                                    value={typeFilter}
                                    onChange={setTypeFilter}
                                    options={[
                                        { value: "all", label: "Tous" },
                                        ...Object.entries(NURTURING_TYPE_LABELS).map(([value, label]) => ({ value, label })),
                                    ]}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">
                                    Priorité
                                </label>
                                <Select
                                    fullWidth
                                    value={priorityFilter}
                                    onChange={setPriorityFilter}
                                    options={[
                                        { value: "all", label: "Toutes" },
                                        { value: "high", label: "Haute" },
                                        { value: "medium", label: "Moyenne" },
                                        { value: "low", label: "Faible" },
                                    ]}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* List */}
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="w-6 h-6 border-2 border-ink border-t-transparent rounded-full motion-safe:animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-sm text-danger">{error}</div>
                    </div>
                ) : nurturings.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 rounded-2xl border border-dashed border-border">
                        <Store className="h-8 w-8 text-ink-faint mb-3" />
                        <p className="text-sm text-ink-muted mb-4">Aucun nurturing</p>
                        <Button onClick={() => navigate("/nurturings/create")}>
                            Créer un nurturing
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {nurturings.map((nurturing) => (
                            <div
                                key={nurturing._id}
                                onClick={() => navigate(`/nurturings/${nurturing._id}`)}
                                className="rounded-2xl border border-border bg-surface p-4 hover:bg-surface-sunken/60 cursor-pointer transition-colors duration-150"
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-sm font-semibold text-ink truncate">
                                                {nurturing.displayName || nurturing.entityName}
                                            </h3>
                                            <Badge tone={getStatusTone(nurturing.status)}>
                                                {STATUS_LABELS[nurturing.status]}
                                            </Badge>
                                            {nurturing.priority && nurturing.priority !== 'medium' && (
                                                <Badge tone={getPriorityTone(nurturing.priority)}>
                                                    {PRIORITY_LABELS[nurturing.priority]}
                                                </Badge>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-4 text-xs text-ink-muted">
                                            <span>{NURTURING_TYPE_LABELS[nurturing.type]}</span>
                                            {nurturing.enrichmentData?.city && (
                                                <span className="flex items-center gap-1">
                                                    <MapPin className="h-3 w-3" />
                                                    {nurturing.enrichmentData.city}
                                                </span>
                                            )}
                                            <span>Dernier check: {formatDate(nurturing.lastCheckAt)}</span>
                                        </div>

                                        {nurturing.tags && nurturing.tags.length > 0 && (
                                            <div className="flex gap-1 mt-2">
                                                {nurturing.tags.slice(0, 3).map((tag, index) => (
                                                    <span key={index} className="px-2 py-0.5 bg-surface-sunken text-ink-muted rounded-full text-xs">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-6">
                                        {nurturing.alertCount > 0 && (
                                            <div className="flex items-center gap-2">
                                                <BellRing className="h-4 w-4 text-danger" />
                                                <span className="text-sm font-medium text-danger">{nurturing.alertCount}</span>
                                            </div>
                                        )}
                                        {nurturing.lastEventAt && (
                                            <div className="text-xs text-ink-faint">
                                                {formatDateTime(nurturing.lastEventAt)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
